import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getServices } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, Mail, Pencil, Trash2, Shield, User, Scissors } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    name: '',
    password: '',
    role: 'ASISTENTE',
    is_active: true,
    service_ids: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      // Laravel devuelve {data: [...]}, Axios extrae el data, entonces accedemos a .data.data
      const allUsers = Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []);
      setUsers(allUsers.filter((u) => u.role === 'ADMIN' || u.role === 'ASISTENTE'));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar usuarios'));
    }
  };

  const fetchServices = async () => {
    try {
      const response = await getServices(true);
      setServices(Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar servicios'));
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchServices();
  }, []);

  const handleOpenDrawer = (user = null) => {
    setSelectedUser(user);
    if (user) {
      setFormData({
        email: user.email || '',
        phone: user.phone || '',
        name: user.name,
        password: '',
        role: user.role,
        is_active: user.is_active,
        service_ids: Array.isArray(user.services) ? user.services.map((s) => String(s.id)) : [],
      });
    } else {
      setFormData({
        email: '',
        phone: '',
        name: '',
        password: '',
        role: 'ASISTENTE',
        is_active: true,
        service_ids: [],
      });
    }
    setDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (selectedUser) {
        const updateData = {
          name: formData.name,
          role: formData.role,
          is_active: formData.is_active,
          service_ids: (formData.service_ids || []).map((id) => Number(id)),
        };
        // Solo incluir email si está presente
        if (formData.email) {
          updateData.email = formData.email;
        }
        // Solo incluir phone si está presente
        if (formData.phone) {
          updateData.phone = formData.phone;
        }
        await updateUser(selectedUser.id, updateData);
        toast.success('Usuario actualizado');
      } else {
        const createData = {
          name: formData.name,
          password: formData.password,
          role: formData.role,
          service_ids: (formData.service_ids || []).map((id) => Number(id)),
        };
        // Para PACIENTE, phone es requerido y email es opcional
        // Para otros roles, email es requerido y phone es opcional
        if (formData.role === 'PACIENTE') {
          createData.phone = formData.phone;
          if (formData.email) {
            createData.email = formData.email;
          }
        } else {
          createData.email = formData.email;
          if (formData.phone) {
            createData.phone = formData.phone;
          }
        }
        await createUser(createData);
        toast.success('Usuario creado');
      }
      setDrawerOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al guardar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error('No puedes eliminarte a ti mismo');
      return;
    }
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      await deleteUser(id);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al eliminar'));
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Personal</h1>
          <p className="text-[#5D5C61] mt-1">Administra barberos y estilistas del sistema</p>
        </div>
          <Button
            className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
            onClick={() => handleOpenDrawer()}
            data-testid="users-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo integrante
          </Button>
      </div>

      <div className="grid gap-4">
        {users.map((user) => (
          <Card
            key={user.id}
            className="bg-white border-slate-100"
            data-testid={`user-card-${user.id}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg ${
                    user.role === 'ADMIN' ? 'bg-[#9E2A2B]' : 'bg-[#335C67]'
                  }`}>
                    {user.name.charAt(0)}
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {user.name}
                      {user.id === currentUser?.id && (
                        <Badge variant="outline" className="text-xs">Tú</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {user.email || 'Sin correo'}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={
                    user.role === 'ADMIN'
                      ? 'bg-[#9E2A2B] hover:bg-[#9E2A2B]'
                      : 'bg-[#335C67] hover:bg-[#335C67]'
                  }>
                    <Shield className="w-3 h-3 mr-1" />
                    {user.role}
                  </Badge>
                  <Badge variant={user.is_active ? 'default' : 'secondary'} className={
                    user.is_active ? 'bg-[#588157] hover:bg-[#588157]' : ''
                  }>
                    {user.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="hover:bg-[#F4F1EA]"
                  onClick={() => handleOpenDrawer(user)}
                  data-testid={`user-edit-${user.id}`}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                {user.id !== currentUser?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#9E2A2B] hover:bg-[#9E2A2B]/10"
                    onClick={() => handleDelete(user.id)}
                    data-testid={`user-delete-${user.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {users.length === 0 && (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#2B2D42]">No hay usuarios</h3>
          <p className="text-[#5D5C61] mt-1">Crea el primer usuario del sistema</p>
        </div>
      )}

      {/* User Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md" data-testid="user-drawer">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">
              {selectedUser ? 'Editar personal' : 'Nuevo personal'}
            </SheetTitle>
            <SheetDescription>
              {selectedUser ? 'Modifica los datos del integrante' : 'Crea un nuevo integrante del personal'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#8D99AE]" />
                Nombre completo
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Juan Perez"
                data-testid="user-name-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#8D99AE]" />
                Correo electronico
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="empleado@reserveo.com"
                data-testid="user-email-input"
                required
              />
            </div>

            {!selectedUser && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[#2B2D42] font-medium">Contraseña *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-sm h-8 px-3 bg-[#335C67] text-white hover:bg-[#244048] border-[#335C67]"
                    onClick={() => {
                      // Generar contraseña aleatoria de 8 caracteres
                      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
                      let password = '';
                      for (let i = 0; i < 8; i++) {
                        password += chars.charAt(Math.floor(Math.random() * chars.length));
                      }
                      setFormData({ ...formData, password });
                      toast.success('Contraseña generada. Copia la contraseña que aparece abajo.');
                    }}
                  >
                    🔑 Generar Contraseña
                  </Button>
                </div>
                {formData.password ? (
                  <div className="space-y-2">
                    <div className="p-3 bg-[#F4F1EA] rounded-lg border-2 border-[#E09F3E]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-[#2B2D42]">Contraseña generada:</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs h-auto py-1 px-2"
                          onClick={() => {
                            navigator.clipboard.writeText(formData.password);
                            toast.success('Contraseña copiada al portapapeles');
                          }}
                        >
                          📋 Copiar
                        </Button>
                      </div>
                      <div className="text-lg font-mono font-bold text-[#335C67] bg-white p-2 rounded border">
                        {formData.password}
                      </div>
                      <p className="text-xs text-[#8D99AE] mt-2">
                        ⚠️ <strong>Importante:</strong> Copia esta contraseña ahora. No se mostrará nuevamente después de guardar.
                      </p>
                    </div>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      data-testid="user-password-input"
                      required={!selectedUser}
                      minLength={6}
                      className="mt-2"
                    />
                    <p className="text-xs text-[#8D99AE]">Puedes editar la contraseña manualmente si lo deseas</p>
                  </div>
                ) : (
                  <>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      data-testid="user-password-input"
                      required={!selectedUser}
                      minLength={6}
                    />
                    <p className="text-xs text-[#8D99AE]">Mínimo 6 caracteres. O haz clic en "Generar automática" arriba.</p>
                  </>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#8D99AE]" />
                Rol
              </Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ASISTENTE">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Scissors className="w-4 h-4 text-[#8D99AE]" />
                Servicios que puede atender
              </Label>
              <div className="flex flex-wrap gap-3">
                {services.map((svc) => {
                  const idStr = String(svc.id);
                  const checked = Array.isArray(formData.service_ids) && formData.service_ids.includes(idStr);
                  return (
                    <label
                      key={svc.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? 'border-[#335C67] bg-[#335C67]/5'
                          : 'border-slate-200 hover:border-[#335C67]/30'
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => {
                          setFormData((prev) => {
                            const prevIds = Array.isArray(prev.service_ids) ? prev.service_ids : [];
                            if (value) {
                              return {
                                ...prev,
                                service_ids: prevIds.includes(idStr) ? prevIds : [...prevIds, idStr],
                              };
                            }
                            return { ...prev, service_ids: prevIds.filter((x) => x !== idStr) };
                          });
                        }}
                      />
                      <span className="text-sm text-[#2B2D42]">
                        {svc.name} ({svc.duration_minutes}m)
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {selectedUser && (
              <div className="flex items-center justify-between py-4 px-4 bg-[#F4F1EA] rounded-lg">
                <div>
                  <Label className="font-medium">Usuario activo</Label>
                  <p className="text-sm text-[#5D5C61]">Permite acceso al sistema</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                  data-testid="user-active-switch"
                />
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
              disabled={isLoading}
              data-testid="user-save-btn"
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
